import React, { useMemo, useState } from 'react';
import cn from 'classnames';
import moment from 'moment';
import { extendMoment } from 'moment-range';

import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToInteger from 'utils/converters/fromAnyToInteger';
import { fromAnyDateToDate, fromAnyDateToZulu } from 'utils/converters/time/from.util';
import { formatLocalizedTime } from 'utils/date/format.util';
import windowScrollTo from 'utils/dom/windowScrollTo.util';
import call from 'utils/functions/call.util';
import logger from 'utils/logger.util';

const L = logger('TimeSlot'); // eslint-disable-line no-unused-vars

const PERIOD = Object.freeze({
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
});

const SLOT_KEY_NOON = '12:00';
const SLOT_KEY_EVENING = '18:00';
const SLOT_KEY_NIGHT = '22:00';
// Use local timezone from browser for slot key.
// using (fromAnyDateToZulu) caused 1h shift bug between selection and appointment card
const fromAnyDateToSlotKey = (slot) => moment(slot).format('HH:mm');

const generateSlots = (options) => {
  const isMorning = PERIOD.morning === options?.timePeriod;
  const isAfternoon = PERIOD.afternoon === options?.timePeriod;
  const isEvening = PERIOD.evening === options?.timePeriod;

  if (!isMorning && !isAfternoon && !isEvening) return asArray();

  const [openHour, openMinute] = fromAnyToTrimmed(options?.openTime).split(':');
  const [closeHour, closeMinute] = fromAnyToTrimmed(options?.closeTime).split(':');

  const bgn = moment(options?.selectedDate)
    .set('hour', fromAnyToInteger(openHour))
    .set('minute', fromAnyToInteger(openMinute))
    .set('second', 0)
    .subtract(1, 'hours');

  const end = moment(options?.selectedDate)
    .set('hour', fromAnyToInteger(closeHour))
    .set('minute', fromAnyToInteger(closeMinute))
    .set('second', 0)
    .add(2, 'hours');

  const range = extendMoment(moment).range(bgn, end);

  const slots = Array.from(range?.by('minutes', { step: 15 })).map((slot) => {
    const key = fromAnyDateToSlotKey(slot);
    const [hour, minute] = key.split(':');

    return {
      zulu: fromAnyDateToZulu(slot),
      display: formatLocalizedTime(slot),
      key,
      hour: fromAnyToInteger(hour),
      minute: fromAnyToInteger(minute),
    };
  });

  if (isMorning) return slots.filter(({ key }) => SLOT_KEY_NOON > key);
  if (isAfternoon) return slots.filter(({ key }) => SLOT_KEY_NOON <= key && key < SLOT_KEY_EVENING);
  if (isEvening) return slots.filter(({ key }) => SLOT_KEY_EVENING <= key && key <= SLOT_KEY_NIGHT);
};

const PeriodButtons = ({ slots, value, onSelect }) => {
  const valueKey = fromAnyDateToSlotKey(value);
  return (
    <div
      data-bem="PeriodButtons"
      data-slot-value={fromAnyDateToZulu(value)}
      data-slot-key={valueKey}
      className="time-btn-container"
    >
      {asArray(slots).map((slot) => (
        <div
          className={cn({
            'the-time-shape': true,
            selected: valueKey === slot.key,
          })}
          role="button"
          tabIndex={0}
          key={slot.key}
          data-is-selected={String(valueKey === slot.key)}
          data-slot-display={slot.display}
          data-slot-key={slot.key}
        >
          <div className="item-option">
            <div onClick={() => call('PeriodButtons.onSelect', onSelect, { value: slot })} role="button" tabIndex="-1">
              {slot.display}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const TimeSlot = ({ selectedDate, openingHours, selectedTime, onSelect }) => {
  const hours = fromAnyDateToDate(selectedTime).getHours();

  const [timePeriod, setTimePeriod] = useState(
    12 > hours ? PERIOD.morning : 18 > hours ? PERIOD.afternoon : PERIOD.evening,
  );

  const openTime = openingHours?.[moment(selectedDate).weekday()]?.open_time;

  const slots = useMemo(
    () => generateSlots({ selectedDate, timePeriod, openTime, closeTime: '19:45:00' }),
    [openTime, selectedDate, timePeriod],
  );

  return (
    <div data-bem="TimeSlot" className="time-slots">
      {timePeriod === PERIOD.morning ? (
        <PeriodButtons slots={slots} onSelect={onSelect} value={selectedTime} />
      ) : (
        <div
          className="morning-btn"
          role="button"
          tabIndex="-1"
          onClick={() => {
            setTimePeriod(PERIOD.morning);
            windowScrollTo(0, 100);
          }}
        >
          <p>Morning</p>
        </div>
      )}

      {timePeriod === PERIOD.afternoon ? (
        <PeriodButtons slots={slots} onSelect={onSelect} value={selectedTime} />
      ) : (
        <div
          className="morning-btn"
          role="button"
          tabIndex="-2"
          onClick={() => {
            setTimePeriod(PERIOD.afternoon);
            windowScrollTo(0, 400);
          }}
        >
          <p>Afternoon</p>
        </div>
      )}

      {timePeriod === PERIOD.evening ? (
        <PeriodButtons slots={slots} onSelect={onSelect} value={selectedTime} />
      ) : (
        <div
          className="morning-btn"
          role="button"
          tabIndex="-3"
          onClick={() => {
            setTimePeriod(PERIOD.evening);
            windowScrollTo(0, 600);
          }}
        >
          <p>Evening</p>
        </div>
      )}
    </div>
  );
};

export default TimeSlot;
